import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Link2, Plus } from 'lucide-react';

// Available source types for citations
const SOURCE_TYPES = [
  { value: 'Help center', label: 'Help center' },
  { value: 'Linear', label: 'Linear' },
  { value: 'Notion', label: 'Notion' },
  { value: 'Slack', label: 'Slack' },
] as const;

interface CitationInputProps {
  onInsert: (formatted: string) => void;
}

/**
 * Popover for inserting citations in the format [Label: URL]
 */
export function CitationInput({ onInsert }: CitationInputProps) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('Help center');

  const handleInsert = () => {
    if (!url.trim()) return;

    const formatted = `[${label}: ${url.trim()}]`;
    onInsert(formatted);

    // Reset and close
    setUrl('');
    setLabel('Help center');
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && url.trim()) {
      e.preventDefault();
      handleInsert();
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1.5"
        >
          <Link2 className="h-3.5 w-3.5" />
          Add Citation
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="start">
        <div className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-medium leading-none">Insert Citation</h4>
            <p className="text-sm text-muted-foreground">
              Add a source link that will be formatted for Slack.
            </p>
          </div>

          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="citation-url">URL</Label>
              <Input
                id="citation-url"
                placeholder="https://help.atlas.kitchen/..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="citation-label">Source Type</Label>
              <Select value={label} onValueChange={setLabel}>
                <SelectTrigger>
                  <SelectValue placeholder="Select source type" />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_TYPES.map((source) => (
                    <SelectItem key={source.value} value={source.value}>
                      {source.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-between items-center pt-2">
            <p className="text-xs text-muted-foreground">
              Preview: <code className="bg-muted px-1 rounded">[{label || 'Source'}: URL]</code>
            </p>
            <Button
              type="button"
              size="sm"
              onClick={handleInsert}
              disabled={!url.trim()}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Insert
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default CitationInput;
